import { type ReactElement } from "react"

import { Typography } from "@synapse-analytics/synapse-ui"

import type { InfoRowProps } from "../interface"

import styles from "./InfoRow.module.scss"

/**
 * Layout component used to render a labeled row within a settings page.
 *
 * It displays a row title on the left and arbitrary content (typically form elements or actions)
 * on the right. This component is intended to be reusable for displaying organization or user
 * info fields in a structured format.
 *
 * @param rowTitle - The label or name of the row, displayed on the left side.
 * @param children - The content to render on the right side of the row (e.g., input, button).
 * @param className - Optional custom class name for applying additional styling around child components.
 *
 *
 * @returns A `ReactElement` that renders a two-column row with a title and action/content.
 */
export function InfoRow(props: Readonly<InfoRowProps>): ReactElement {
  const { rowTitle, children, className } = props

  return (
    <div className={styles.infoRow}>
      <div className={styles.rowName}>
        <Typography variant="a">{rowTitle}</Typography>
      </div>

      <div className={`${styles.rowAction} ${className}`}>{children}</div>
    </div>
  )
}
